import { Shimmer, ShimmerElementType, Stack } from "@fluentui/react";
import { IChartProps } from "@fluentui/react-charting";
import { OptionOnSelectData } from "@fluentui/react-components";
import { Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import CustomHorizontalBarChart from "../../../../BasicComponents/CustomHorizontalBarChart";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent } from "../../../../helper/AppInsightsHelper";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import CustomCard from "../../../../SharedComponents/CustomCard";
import {
  getFeatureDataSelector,
  useGetFeatureProductDataDetailsMutation
} from "../../../../slices/ExploreProducts.slice";
import { useAppSelector } from "../../../../store/hooks";
import CustomDropdown from "../../Common/CustomDropdown";
import { FeaturesByCommonNameProps } from "../../Common/ProductView.interfaces";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";

const INITIAL_FEATURE: OptionOnSelectData = { optionText: "", optionValue: "", selectedOptions: [] };
const shimmerElements = [
  { type: ShimmerElementType.line, height: 20, width: "15%" },
  { type: ShimmerElementType.gap, height: 20, width: "70%" },
  { type: ShimmerElementType.line, height: 20, width: "15%" }
];

const shimmerLine = [{ type: ShimmerElementType.line, height: 20 }];
const shimmerColors = { shimmer: "rgb(222, 236, 249)" };
const shimmerStyle = { shimmerWrapper: { ".ms-ShimmerGap-root": { background: "#faf9f8" } } };

const shimmerDropdown = (
  <Shimmer
    shimmerColors={{ shimmer: "var(--shimmer-color)" }}
    styles={{ root: { width: "25%", height: 32 }, shimmerWrapper: { borderRadius: "var(--borderRadiusMedium)" } }}
    shimmerElements={[{ type: ShimmerElementType.line, width: "15%", height: 32 }]}
  />
);

const cardStyles = {
  root: { flex: 1, height: "100%" },
  cardDetailsContent: { padding: 13 },
  cardHeaderStart: { flex: 1 },
  cardHeaderTitleContainer: { flexBasis: "40%" }
};

const shimmerLoader = (
  <div style={{ height: 362 }}>
    <Stack tokens={{ childrenGap: 7 }}>
      <Shimmer shimmerElements={shimmerElements} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer width="100%" shimmerElements={shimmerLine} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer shimmerElements={shimmerElements} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer width="80%" shimmerElements={shimmerLine} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer shimmerElements={shimmerElements} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer width="72%" shimmerElements={shimmerLine} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer shimmerElements={shimmerElements} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer width="55%" shimmerElements={shimmerLine} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer shimmerElements={shimmerElements} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer width="40%" shimmerElements={shimmerLine} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer shimmerElements={shimmerElements} shimmerColors={shimmerColors} styles={shimmerStyle} />
      <Shimmer width="30%" shimmerElements={shimmerLine} shimmerColors={shimmerColors} styles={shimmerStyle} />
    </Stack>
    <Shimmer
      width="8%"
      shimmerElements={shimmerLine}
      shimmerColors={shimmerColors}
      style={{ display: "flex", justifyContent: "flex-end", height: 30, marginTop: 12 }}
    />
  </div>
);

const formatDataForChart = (data: FeaturesByCommonNameProps[]): IChartProps[] => {
  return data.map(({ name, currentValue }) => {
    return {
      chartTitle: name,
      chartData: [
        {
          legend: name,
          horizontalBarChartdata: {
            x: currentValue,
            y: 10000
          },
          color: "#0078D4"
        }
      ]
    };
  });
};

const RenderCustomDropdown = ({
  onSelectedOption,
  selectedFeature: externalSelectedFeature
}: {
  onSelectedOption?: (data: OptionOnSelectData) => void;
  selectedFeature?: OptionOnSelectData;
}) => {
  const [internalSelectedFeature, setInternalSelectedFeature] = useState<OptionOnSelectData>(INITIAL_FEATURE);
  const { data, isLoading, isSuccess } = useAppSelector((state) => getFeatureDataSelector(state));
  const filters = useContext(ProductViewFiltersContext);

  // Use external selectedFeature if provided, otherwise use internal state
  const selectedFeature = externalSelectedFeature ?? internalSelectedFeature;

  const customProps = {
    productName: filters?.productName,
    productFilters: JSON.stringify(filters)
  };

  const onSelectedFeature = useCallback(
    (option: OptionOnSelectData) => {
      // Only update internal state if no external selectedFeature is provided
      if (externalSelectedFeature === undefined) {
        setInternalSelectedFeature(option);
      }

      logEvent("Segmentation Tab - Features Telemetry", "Product View Page", "SegmentationTab", customProps);
      if (onSelectedOption) {
        onSelectedOption(option);
      }
    },
    [externalSelectedFeature, onSelectedOption, customProps]
  );

  useEffect(() => {
    // Only reset internal state if no external selectedFeature is provided
    if (externalSelectedFeature === undefined) {
      setInternalSelectedFeature(INITIAL_FEATURE);
    }
  }, [
    externalSelectedFeature,
    filters.hasPartner,
    filters.industry,
    filters.isCAT,
    filters.isFastTrackStrategic,
    filters.isSST,
    filters.productName,
    filters.region,
    filters.segmentGroup,
    filters.segment,
    filters.subSegment
  ]);

  useEffect(() => {
    if (isSuccess && externalSelectedFeature === undefined) {
      const option = data?.featuresByName.at(0);
      if (option && !selectedFeature.optionValue) {
        setInternalSelectedFeature({
          optionText: option.featureName,
          optionValue: option.featureName,
          selectedOptions: [option.featureId]
        });
      }
    }
  }, [isSuccess, externalSelectedFeature, selectedFeature.optionValue, data?.featuresByName]);

  return (
    (isLoading && <div style={{ flexBasis: "100%" }}>{shimmerDropdown}</div>) ||
    (isSuccess && (
      <CustomDropdown
        onSelectedOption={onSelectedFeature}
        defaultOption={selectedFeature}
        data={(data?.featuresByName || [])
          .filter(
            ({ featureId }, index, currentArray) => currentArray.findIndex((f) => f.featureId === featureId) === index
          )
          .map(({ featureId, featureName }) => ({ value: featureId, text: featureName }))}
        styles={{ root: { flexBasis: "100%" } }}
      />
    ))
  );
};

export const SegmentationTab = () => {
  const [selectedFeature, setSelectedFeature] = useState<OptionOnSelectData>();
  const { data, error, isError, isLoading, isSuccess } = useAppSelector(getFeatureDataSelector);
  const [getData, { isLoading: isLoadingByFeature }] = useGetFeatureProductDataDetailsMutation({
    fixedCacheKey: "getFeatures"
  });
  const filters = useContext(ProductViewFiltersContext);

  const onSelectedFeature = useCallback((option: OptionOnSelectData) => setSelectedFeature(option), []);
  const renderHorizontalBarHeader = useCallback(
    ({ title }: { title: string }) => {
      return isLoading ? <Shimmer styles={PRODUCT_VIEW_STYLES.TAB.SEGMENTATION_TAB.CSS_STYLES.shimmerStyles} /> : title;
    },
    [isLoading]
  );

  const displayBarChart = useCallback(
    (dataForChart?: FeaturesByCommonNameProps[]) =>
      isSuccess && <CustomHorizontalBarChart data={formatDataForChart(dataForChart || [])} />,
    [isSuccess]
  );
  const chartData = useMemo(
    () => [
      {
        title: "CurrentValue by Region",
        data:
          data?.featuresByRegion?.map(({ currentValue, currentMDPUValue, region: name }) => ({
            currentValue,
            currentMDPUValue,
            name
          })) ?? [],
        kustoLink: data?.featuresByRegionKustoLink
      },
      {
        title: "CurrentValue by Industry",
        data:
          data?.featuresByIndustry?.map(({ currentValue, currentMDPUValue, industry: name }) => ({
            currentValue,
            currentMDPUValue,
            name
          })) ?? [],
        kustoLink: data?.featuresByIndustryKustoLink
      },
      {
        title: "CurrentValue by SegmentGroup",
        data:
          data?.featuresBySegmentGroup?.map(({ currentValue, currentMDPUValue, segmentGroup: name }) => ({
            currentValue,
            currentMDPUValue,
            name
          })) ?? [],
        kustoLink: data?.featuresBySegmentGroupKustoLink
      }
    ],
    [data]
  );

  useEffect(() => {
    if (selectedFeature && filters.productName) {
      const requestData = {
        featureId: selectedFeature.optionValue,
        hasPartner: filters.hasPartner,
        industry: filters.industry,
        isCAT: filters.isCAT,
        isFastTrackStrategic: filters.isFastTrackStrategic,
        isSST: filters.isSST,
        productName: filters.productName,
        region: filters.region,
        segmentGroup: filters.segmentGroup,
        segment: filters.segment,
        subSegment: filters.subSegment
      };
      getData(requestData);
    }
  }, [selectedFeature, filters.productName, getData, filters]);

  if (!isLoading && isError) {
    return (
      <Stack tokens={{ padding: "10px 13px 13px 13px", childrenGap: 13 }}>
        <RenderCustomDropdown onSelectedOption={onSelectedFeature} selectedFeature={selectedFeature} />
        <DisplayAPIsException error={error} height={345} />
      </Stack>
    );
  }

  return (
    <Stack tokens={{ padding: "10px 13px 13px 13px", childrenGap: 13 }}>
      <RenderCustomDropdown onSelectedOption={onSelectedFeature} selectedFeature={selectedFeature} />
      <Grid container spacing={{ xs: 1, sm: 2, md: 2 }} sx={{ marginTop: "0 !important" }}>
        {chartData.map(({ title, data, kustoLink }) => (
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6} key={crypto.randomUUID()}>
            <CustomCard
              cardHeaderTitle={renderHorizontalBarHeader({ title })}
              styles={cardStyles}
              kustoLink={kustoLink}
            >
              {isLoading || isLoadingByFeature
                ? shimmerLoader
                : displayBarChart(
                    data.map(({ currentValue, currentMDPUValue, name }) => ({ currentValue, currentMDPUValue, name }))
                  )}
            </CustomCard>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};
